import { useEffect, useRef } from "react";
import {
  View,
  Animated,
  StyleSheet,
  Platform,
  useWindowDimensions,
  type TextStyle,
} from "react-native";
import { SafeAreaView } from "react-native-safe-area-context";
import { ArrowRight } from "lucide-react-native";
import type { NativeStackScreenProps } from "@react-navigation/native-stack";
import type { RootStackParamList } from "../App";
import { GlassButton, InviteGlassCard } from "@louise/moon-ui";
import { useTranslations } from "@src/packages/i18n";
import { GradientOrb } from "@src/components/ui/GradientOrb";
import { useStytchUser } from "../utils/useStytch";
import { useReferral } from "../referral/ReferralContext";

type Props = NativeStackScreenProps<RootStackParamList, "PeerInvite">;

const BG_DRIFT_DURATION = 10000;

type TextStyleWithWebExtras = TextStyle & {
  backgroundImage?: string;
  WebkitBackgroundClip?: string;
  WebkitTextFillColor?: string;
  backgroundClip?: string;
  backgroundSize?: string;
  animation?: string;
};

const DARK = {
  text: "#FFFFFF",
  textMid: "rgba(255, 255, 255, 0.6)",
};

export default function PeerInviteLandingScreen({ navigation, route }: Props) {
  const t = useTranslations();
  const { width: viewportWidth } = useWindowDimensions();
  const isCompact = viewportWidth < 600;
  const { captureLanding } = useReferral();
  const { user } = useStytchUser();
  const code = route.params?.code;

  const fade = useRef(new Animated.Value(0)).current;
  const bgPan = useRef(new Animated.Value(0)).current;

  useEffect(() => {
    if (code) captureLanding(code);
  }, [captureLanding, code]);

  useEffect(() => {
    Animated.timing(fade, {
      toValue: 1,
      duration: 600,
      useNativeDriver: true,
    }).start();
  }, [fade]);

  useEffect(() => {
    Animated.loop(
      Animated.sequence([
        Animated.timing(bgPan, {
          toValue: 1,
          duration: BG_DRIFT_DURATION,
          useNativeDriver: true,
        }),
        Animated.timing(bgPan, {
          toValue: 0,
          duration: BG_DRIFT_DURATION,
          useNativeDriver: true,
        }),
      ]),
    ).start();
  }, [bgPan]);

  const bgTranslateX = bgPan.interpolate({
    inputRange: [0, 1],
    outputRange: [-30, 30],
  });
  const bgTranslateY = bgPan.interpolate({
    inputRange: [0, 1],
    outputRange: [-15, 15],
  });
  const bgScale = bgPan.interpolate({
    inputRange: [0, 0.5, 1],
    outputRange: [1.1, 1.16, 1.1],
  });

  const handleContinue = () => {
    navigation.reset({
      index: 0,
      routes: [{ name: user ? "Home" : "Onboarding" }],
    });
  };

  return (
    <View style={styles.root}>
      <Animated.Image
        source={require("../assets/images/beta-invite-bg.avif")}
        style={[
          StyleSheet.absoluteFill,
          {
            width: "120%",
            height: "120%",
            left: "-10%",
            top: "-10%",
            transform: [
              { translateX: bgTranslateX },
              { translateY: bgTranslateY },
              { scale: bgScale },
            ],
          },
        ]}
        resizeMode="cover"
      />

      <View
        style={[
          StyleSheet.absoluteFill,
          { backgroundColor: "rgba(0, 0, 0, 0.35)" },
        ]}
      />

      <SafeAreaView style={{ flex: 1 }}>
        <View style={[styles.wrapper, isCompact && styles.wrapperCompact]}>
          <Animated.View
            style={{
              alignItems: "center",
              marginBottom: -48,
              zIndex: 10,
              opacity: fade,
            }}
          >
            <GradientOrb size={96} />
          </Animated.View>

          <InviteGlassCard style={{ opacity: fade }}>
            <Animated.Text
              style={
                {
                  ...styles.title,
                  ...(Platform.OS === "web" && {
                    backgroundImage:
                      "linear-gradient(135deg, rgba(255,255,255,1) 0%, rgba(255,255,255,0.6) 40%, rgba(212,165,116,0.9) 60%, rgba(255,255,255,0.8) 80%, rgba(255,255,255,1) 100%)",
                    WebkitBackgroundClip: "text",
                    WebkitTextFillColor: "transparent",
                    backgroundClip: "text",
                    backgroundSize: "200% 200%",
                    animation: "inviteTitleShimmer 6s ease-in-out infinite",
                  }),
                } satisfies TextStyleWithWebExtras
              }
            >
              {t("you.invite_friend.landing_title")}
            </Animated.Text>

            <Animated.Text className="font-dm-sans" style={styles.body}>
              {t("you.invite_friend.landing_subtitle")}
            </Animated.Text>

            <Animated.View style={{ width: "100%" }}>
              <GlassButton
                variant="shimmer"
                label={t("you.invite_friend.landing_cta")}
                onPress={handleContinue}
                trailing={
                  <ArrowRight size={16} color="#FFFFFF" strokeWidth={2} />
                }
                style={{ width: "100%" }}
              />
            </Animated.View>
          </InviteGlassCard>
        </View>
      </SafeAreaView>
    </View>
  );
}

const styles = StyleSheet.create({
  root: {
    flex: 1,
    width: "100%",
    height: "100%",
    overflow: "hidden",
    backgroundColor: "#0A0A0A",
  },
  wrapper: {
    flex: 1,
    justifyContent: "center",
    alignItems: "center",
    paddingHorizontal: 20,
  },
  wrapperCompact: {
    justifyContent: "flex-end",
    paddingHorizontal: 16,
    paddingBottom: 12,
  },
  title: {
    fontSize: 42,
    fontFamily: "Tobias-Thin",
    color: DARK.text,
    textAlign: "center",
    lineHeight: 50,
    letterSpacing: -1,
    marginBottom: 28,
  },
  body: {
    fontSize: 15,
    color: DARK.textMid,
    textAlign: "center",
    lineHeight: 26,
    marginBottom: 48,
  },
});
